import { ChipProps } from '@mui/material';
import {
  BillingAgingBucket,
  BillingLineDto,
  BillingLineEventType,
  BillingLineStatus,
  BillingLineType,
  BillingPaymentMethod,
  BillingReminderOutcome,
  BillingReminderTemplate,
  BillingStatusTab,
  BillingWaiveReason,
  ConsultationCode,
  ConventionSector,
  ReminderChannel
} from './billingLine.model';
import {
  holdReleaseReasonLabelKey,
  stripeDisputeReasonLabelKey,
  stripeRefundReasonLabelKey
} from './deskPayment.labels';
import { paymentLinkUnbookedReasonLabelKey } from './paymentLink.labels';

/**
 * Single source of i18n keys and theme colors for the Billing V0 enums, shared by the PDV block,
 * the chase dashboard and the config screens. Follows the `calculationTypeLabelKey` precedent:
 * total `Record<>`s so an unmapped enum value fails the build. All keys live under
 * `doctor-dashboard.billing.*` (see ignored/newTranslations.json).
 */

const KEY_PREFIX = 'doctor-dashboard.billing';

export const billingLineStatusLabelKey = (status: BillingLineStatus) =>
  `${KEY_PREFIX}.status.${status}` as const;

/**
 * A line's status chip label, with one carve-out: `EXEMPT` from
 * `waiveReason: 'INTERVENTION_CANCELED'` reads as its own key rather than the generic
 * "Exempt" — storage stays `EXEMPT` (spec), but the patient isn't exempt, the intervention
 * was cancelled, and the chip should say that instead of implying an exemption.
 */
export const billingLineDisplayStatusLabelKey = (
  line: Pick<BillingLineDto, 'storedStatus' | 'waiveReason'>
) =>
  line.storedStatus === 'EXEMPT' && line.waiveReason === 'INTERVENTION_CANCELED'
    ? (`${KEY_PREFIX}.status.EXEMPT_INTERVENTION_CANCELED` as const)
    : billingLineStatusLabelKey(line.storedStatus);

export const billingStatusTabLabelKey = (tab: BillingStatusTab) =>
  `${KEY_PREFIX}.statusTab.${tab}` as const;

export const billingLineTypeLabelKey = (type: BillingLineType) =>
  `${KEY_PREFIX}.lineType.${type}` as const;

export const consultationCodeLabelKey = (code: ConsultationCode) =>
  `${KEY_PREFIX}.consultationCode.${code}` as const;

export const paymentMethodLabelKey = (method: BillingPaymentMethod) =>
  `${KEY_PREFIX}.paymentMethod.${method}` as const;

export const waiveReasonLabelKey = (reason: BillingWaiveReason) =>
  `${KEY_PREFIX}.waiveReason.${reason}` as const;

/**
 * The same two namespaces for a token that is not typed: the audit trail words its `details`
 * from raw strings, and a value added backend-side has to degrade to its own name rather than
 * break the build (cf. {@link billingLineEventTypeLabelKey}). Kept as builders of their own so
 * the enum-typed pair above stays exhaustive — widening those would let a misspelt member
 * through as a silently missing key.
 */
export const rawPaymentMethodLabelKey = (method: string) =>
  `${KEY_PREFIX}.paymentMethod.${method}` as const;

export const rawWaiveReasonLabelKey = (reason: string) =>
  `${KEY_PREFIX}.waiveReason.${reason}` as const;

/** Why a due reminder was not sent — REMINDER_SKIPPED_UNDELIVERABLE's `details.reason`. */
export const reminderSkipReasonLabelKey = (reason: string) =>
  `${KEY_PREFIX}.skipReason.${reason}` as const;

/**
 * The wording of an audit event's `details.reason`, whose vocabulary is the event type's and
 * not the detail key's: a skip reason on REMINDER_SKIPPED_UNDELIVERABLE, a hold-release reason
 * on STRIPE_RELEASED and the renewal-result events, Stripe's own refund / dispute reason on the
 * events it writes itself, an unbooking reason on STRIPE_LINK_PAYMENT_UNBOOKED, a waive reason
 * everywhere else.
 * STRIPE_LINK_PAYMENT_REFUNDED gives back what a link collected and speaks the refund's own
 * vocabulary, not ours, so it reads from the same namespace as the refund. STRIPE_REFUND_FAILED
 * carries no reason at all today — its details are the Stripe ids, the refund's status and the
 * compensated amount — and is routed with the refunds defensively, so that a reason Stripe may
 * start sending is worded in the vocabulary it would come from rather than as a waive reason;
 * STRIPE_DISPUTE_CLOSED is routed with STRIPE_DISPUTE_FLAGGED for exactly that reason.
 * Stated here, where the event types are, so the trail never spells a namespace out by hand and
 * cannot drift from the key builders.
 */
export const billingLineEventReasonLabelKey = (
  eventType: BillingLineEventType,
  reason: string
) => {
  switch (eventType) {
    case 'REMINDER_SKIPPED_UNDELIVERABLE':
      return reminderSkipReasonLabelKey(reason);
    case 'STRIPE_RELEASED':
    case 'STRIPE_HOLD_RENEWED':
    case 'STRIPE_HOLD_RENEWAL_FAILED':
      return holdReleaseReasonLabelKey(reason);
    case 'STRIPE_REFUND_SYNCED':
    case 'STRIPE_REFUND_FAILED':
    case 'STRIPE_LINK_PAYMENT_REFUNDED':
      return stripeRefundReasonLabelKey(reason);
    case 'STRIPE_DISPUTE_FLAGGED':
    case 'STRIPE_DISPUTE_CLOSED':
      return stripeDisputeReasonLabelKey(reason);
    case 'STRIPE_LINK_PAYMENT_UNBOOKED':
      return paymentLinkUnbookedReasonLabelKey(reason);
    default:
      return rawWaiveReasonLabelKey(reason);
  }
};

/**
 * Tone of an audit-trail row's event chip, keyed on the event type alone and never on its details.
 * Everything the trail records is history, so the rows are uniform by default; only the events
 * that cost the hospital money are tinted. Red is kept for the two that need somebody to act — a
 * link payment HoopCare could not book onto the line, and a chargeback with an evidence deadline.
 * A refund is settled bookkeeping, so STRIPE_REFUND_SYNCED is only marked as money leaving — amber
 * whether it was booked or merely recorded because Stripe already reported it failed or canceled,
 * a distinction the trail states in words and not in colour, as it does the STRIPE_REFUND_FAILED
 * that walks a booked one back. STRIPE_LINK_PAYMENT_REFUNDED stays untinted although its unbooked
 * sibling is red: that money never reached this line's ledger, so giving it back costs the
 * hospital nothing and closes the very row the red chip flagged.
 *
 * A closed chargeback is amber for the same reason a refund is, and can be nothing else: won and
 * lost are one event type, told apart by details this function may not read, so a red chip would
 * announce a loss on every dispute the hospital won. The trail states the outcome in words
 * instead — Stripe's own status, and the line it reopened.
 */
export const billingLineEventChipColor = (
  eventType: BillingLineEventType
): ChipProps['color'] => {
  switch (eventType) {
    case 'STRIPE_LINK_PAYMENT_UNBOOKED':
    case 'STRIPE_DISPUTE_FLAGGED':
    case 'STRIPE_HOLD_RENEWAL_FAILED':
      return 'error';
    case 'STRIPE_REFUND_SYNCED':
    case 'STRIPE_DISPUTE_CLOSED':
      return 'warning';
    default:
      return 'default';
  }
};

export const agingBucketLabelKey = (bucket: BillingAgingBucket) =>
  `${KEY_PREFIX}.agingBucket.${bucket}` as const;

export const reminderOutcomeLabelKey = (outcome: BillingReminderOutcome) =>
  `${KEY_PREFIX}.reminderOutcome.${outcome}` as const;

export const reminderChannelLabelKey = (channel: ReminderChannel) =>
  `${KEY_PREFIX}.reminderChannel.${channel}` as const;

export const reminderTemplateLabelKey = (template: BillingReminderTemplate) =>
  `${KEY_PREFIX}.settings.cadence.template.${template}` as const;

export const billingLineEventTypeLabelKey = (eventType: string) =>
  `${KEY_PREFIX}.eventType.${eventType}` as const;

/**
 * Convention sectors reuse the existing convention-status wording from the PDV billing
 * card, hence the `details.billingCard` namespace rather than `billing.*`.
 */
export const conventionSectorLabelKey = (sector: ConventionSector) =>
  `doctor-dashboard.details.billingCard.conventionStatus.${sector}` as const;

export const REMINDER_CHANNELS: readonly ReminderChannel[] = [
  'EMAIL',
  'WHATSAPP',
  'SMS'
];

export const isReminderChannel = (value: string): value is ReminderChannel =>
  (REMINDER_CHANNELS as readonly string[]).includes(value);

/**
 * Splits the backend's `lastReminderChannel` — a comma-joined list of
 * {@link ReminderChannel} names such as `"EMAIL,WHATSAPP"` for the default D+45 step —
 * into its raw tokens. Unknown tokens are kept as-is so a channel added backend-side
 * degrades to its enum name instead of vanishing; use `useReminderChannelsLabel`
 * (billingLine.hooks) to turn them into a translated, comma-separated label.
 */
export const parseReminderChannels = (
  lastReminderChannel: string | undefined
): string[] =>
  lastReminderChannel
    ?.split(',')
    .map((token) => token.trim())
    .filter((token) => !!token) ?? [];

/** MUI Chip color per aging bucket (dashboard aging chips + PDV badges). */
export const AGING_BUCKET_CHIP_COLOR: Record<
  BillingAgingBucket,
  ChipProps['color']
> = {
  CURRENT: 'default',
  OVERDUE: 'warning',
  CRITICAL: 'error'
};

/**
 * MUI Chip color per stored line status: only a settled line is celebrated in green;
 * an exemption is a neutral bookkeeping state (spec), not a positive outcome.
 */
export const LINE_STATUS_CHIP_COLOR: Record<
  BillingLineStatus,
  ChipProps['color']
> = {
  OPEN: 'default',
  PAID: 'success',
  EXEMPT: 'default'
};

/** Ordered option lists for selects / segmented tabs (mockup order). */
export const BILLING_STATUS_TABS: readonly BillingStatusTab[] = [
  'TO_COLLECT',
  'ALL',
  'CURRENT',
  'OVERDUE',
  'CRITICAL',
  'PAID',
  'EXEMPT'
];

export const BILLING_PAYMENT_METHODS: readonly BillingPaymentMethod[] = [
  'CARD',
  'CHEQUE',
  'CASH',
  'BANK_TRANSFER',
  'PART_AMO',
  'PART_AMC',
  'ONLINE',
  'PAID_EXTERNALLY'
];

/**
 * Payment methods a secretary can record manually in the Collect popover.
 * `ONLINE` (Stripe, deferred) and `PAID_EXTERNALLY` (its own action) are
 * system-recorded and excluded here.
 */
export const MANUAL_PAYMENT_METHODS: readonly BillingPaymentMethod[] = [
  'CARD',
  'CHEQUE',
  'CASH',
  'BANK_TRANSFER',
  'PART_AMO',
  'PART_AMC'
];

const BILLING_WAIVE_REASONS: readonly BillingWaiveReason[] = [
  'CMU_C2S',
  'AME',
  'URGENCE',
  'COURTOISIE',
  'INTERVENTION_CANCELED'
];

/**
 * The exemption reasons a line of this type can be given. `INTERVENTION_CANCELED` belongs to
 * the intervention itself: medical-synthesis records it on the acte line when a card hold is
 * released because the surgery was called off, so it is never offered on a consultation.
 */
export const waiveReasonsForLineType = (
  lineType: BillingLineType
): readonly BillingWaiveReason[] =>
  lineType === 'ACTE'
    ? BILLING_WAIVE_REASONS
    : BILLING_WAIVE_REASONS.filter(
        (reason) => reason !== 'INTERVENTION_CANCELED'
      );

export const BILLING_LINE_TYPES: readonly BillingLineType[] = [
  'CONSULTATION',
  'ACTE'
];

/** Ordered convention sectors for the tariff grid (plan order, S1 first). */
export const CONVENTION_SECTORS: readonly ConventionSector[] = [
  'S1',
  'S2_OPTAM_ACO',
  'S2_OPTAM',
  'S2_NON_OPTAM'
];

/**
 * Reminder templates a cadence step can send with, in escalation order. The first entry
 * is what an unset (`null`) step template falls back to backend-side.
 */
export const BILLING_REMINDER_TEMPLATES: readonly BillingReminderTemplate[] = [
  'BILLING_REMINDER',
  'BILLING_REMINDER_FIRM',
  'BILLING_REMINDER_FINAL'
];
